import { defaultError } from '@qpr/client-library';
import api, { ApiRequest, ApiResult } from '../../../api/api-caller';
import initialState, { HighchartsState } from './highchart-component-state';

type APIData = {
  id: string;
  name: string;
  typename: string;
  informationItemIds: string;
  link: string;
};

const fetchMeaData = async (item: HighchartsViewModelProps): Promise<ApiResult> => {
  const req: ApiRequest = {
    method: 'GET'
  };

  let elemId = item.id;

  if (item.query && item.query.length > 0) {
    const meaQuery: ApiRequest = {
      body: {
        query: item.query,
        attributes: 'name, id'
      },
      method: 'POST'
    };
    const metricsResult: ApiResult = await api.asyncCallApi('meaquery', meaQuery);
    if (!metricsResult.ok) {
      return metricsResult;
    }
    if (metricsResult.data.length < 1) {
      return metricsResult;
    }
    elemId = metricsResult.data[0].id;
  }

  let queryString = `measurevalues/${elemId}?${item.period && `period=${item.period}&`}${
    item.periodsBefore && `periodsbefore=${item.periodsBefore}&`
  }${item.periodsAfter && `periodsafter=${item.periodsAfter}&`}${
    item.series?.length > 0 ? `series=${item.series.join('&series=')}` : ''
  }`;
  if (queryString.substr(-1) === '&') queryString = queryString.slice(0, -1);

  const result: ApiResult = await api.asyncCallApi(queryString, req);

  return result;
};

const getMeaData = (result: ApiResult): APIData[] => {
  const { data } = result;

  return data;
};

const errorHandler = (apiResult: ApiResult): HighchartsState => {
  return {
    ...initialState,
    error: apiResult.error || defaultError
  };
};

const doGetData = async (item: HighchartsViewModelProps): Promise<HighchartsState> => {
  try {
    const result = await fetchMeaData(item);

    if (!result.ok) {
      return errorHandler(result);
    }

    const meaData: APIData[] = getMeaData(result);

    return {
      ...initialState,
      data: meaData
    };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface HighchartsViewModelType {
  fetchData(): Promise<HighchartsState>;
  getData(): Promise<HighchartsState>;
}

interface HighchartsViewModelProps {
  id: string;
  query: string;
  series: string[];
  period: string;
  periodsBefore: string;
  periodsAfter: string;
}

const createHighchartsViewModel = (item: HighchartsViewModelProps): HighchartsViewModelType => {
  let data: HighchartsState;
  const cacheKey: string = JSON.stringify(item);

  const fetchData = async (): Promise<HighchartsState> => {
    if (viewModelCache[cacheKey]) {
      data = viewModelCache[cacheKey];
    } else {
      data = item.id !== '' || item.query !== '' ? await doGetData(item) : initialState;
      viewModelCache[cacheKey] = data;
    }
    return data;
  };

  const getData = async (): Promise<HighchartsState> => {
    if (data) {
      return data;
    }
    await fetchData();
    return data;
  };

  return {
    fetchData,
    getData
  };
};

export default createHighchartsViewModel;
